import React from 'react';
import { TranscriptContentProps } from '../../../types/transcript';

export default function TranscriptContent({
    segments,
    activeSegmentId,
    onSegmentClick,
    highlightQuery
}: TranscriptContentProps) {
    const highlightText = (text: string, query: string): React.ReactNode => {
        if (!query) return text;
        
        const parts = text.split(new RegExp(`(${query})`, 'gi'));
        return parts.map((part, index) => 
            part.toLowerCase() === query.toLowerCase() ? (
                <mark key={index} className="bg-yellow-200 px-1 rounded">
                    {part}
                </mark>
            ) : part
        );
    };

    const formatTimestamp = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    if (!segments || segments.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-500">
                <p>No transcript segments available</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {segments.map((segment) => (
                <div
                    key={segment.id}
                    id={`segment-${segment.id}`}
                    className={`group p-4 rounded-lg border-l-4 cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
                        activeSegmentId === segment.id
                            ? 'bg-blue-50 border-l-blue-500 shadow-sm'
                            : 'bg-white border-l-gray-200 hover:border-l-blue-300'
                    }`}
                    onClick={() => onSegmentClick(segment)}
                >
                    <div className="flex items-start gap-3">
                        {segment.startTime > 0 && (
                            <button
                                className={`flex-shrink-0 px-2 py-1 text-xs font-medium rounded transition-colors ${
                                    activeSegmentId === segment.id
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-gray-100 text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-800'
                                }`}
                                title="Click to jump to this timestamp"
                            >
                                {formatTimestamp(segment.startTime)}
                            </button>
                        )}
                        
                        <div className="flex-1 min-w-0">
                            {segment.speaker && (
                                <div className={`text-sm font-semibold mb-1 ${
                                    activeSegmentId === segment.id ? 'text-blue-900' : 'text-gray-700'
                                }`}>
                                    {segment.speaker}
                                </div>
                            )}
                            <p className={`text-sm leading-relaxed ${
                                activeSegmentId === segment.id ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                                {highlightText(segment.text, highlightQuery)}
                            </p>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}